import React, { useState } from "react";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import { toast } from "sonner";
import { api } from "../lib/api";

const supportBlocks = [
  {
    title: "Who We Support",
    body: "Organizations facing manpower shortages, urgent vacancies, and project deadlines.",
  },
  {
    title: "What We Provide",
    body: "Lancers, technical experts, and ready-to-deploy contract resources.",
  },
  {
    title: "How We Work",
    body: "You submit your requirement. Our team reviews it and connects with you shortly.",
  },
];

const bullets = [
  "Quick deployment of expert resources for urgent business and project needs.",
  "Contract-based placement for temporary, short-term, or project-driven roles.",
  "Reliable support for technical work, specialist positions, and execution-focused assignments.",
];

const initialForm = {
  organisation_name: "",
  contact_person: "",
  contact_number: "",
  email: "",
  requirement: "",
  website: "", // honeypot — must stay empty
};

const Consulting = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Client-side validation (server validates as well).
    if (
      !form.organisation_name.trim() ||
      !form.contact_person.trim() ||
      !form.contact_number.trim() ||
      !form.email.trim() ||
      !form.requirement.trim()
    ) {
      const msg = "Please fill in all fields before submitting.";
      setError(msg);
      toast.error(msg);
      return;
    }

    setSubmitting(true);
    try {
      const { data: saved } = await api.post("/contact", form);

      // Local log for the owner's reference (mirrors backend persistence).
      const log = JSON.parse(
        localStorage.getItem("ls_contact_submissions") || "[]"
      );
      log.unshift(saved);
      localStorage.setItem("ls_contact_submissions", JSON.stringify(log));
      // eslint-disable-next-line no-console
      console.log("[Lancers Contact] submission saved:", saved);

      toast.success("Requirement submitted successfully.");
      setSubmitted(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Contact submission failed:", err);
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : "Failed to submit requirement. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout footerNote="© 2026 Lancers Studio. Direct consulting support for contract-based expert deployment.">
      <SEO
        path="/consulting"
        title="Lancers Consulting — Contract Expert Deployment | Lancers Studio"
        description="Lancers Consulting deploys skilled lancers and technical experts on contract for facade and construction organizations facing manpower shortages and urgent vacancies."
      />
      <section
        className="relative px-5 sm:px-8 pt-14 pb-16 max-w-7xl mx-auto"
        data-testid="consulting-section"
      >
        <div
          className="ls-glow"
          style={{ top: "-160px", right: "-160px", opacity: 0.7 }}
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* LEFT */}
          <div className="lg:col-span-7 ls-fade-up">
            <span className="ls-eyebrow" data-testid="consulting-eyebrow">
              Direct Expert Deployment
            </span>
            <h1
              className="font-cinzel gold-text mt-5 leading-tight"
              style={{
                fontSize: "clamp(2.4rem, 5vw, 4rem)",
                letterSpacing: "0.06em",
              }}
              data-testid="consulting-title"
            >
              Lancers Consulting
            </h1>
            <p
              className="mt-6 text-base sm:text-lg leading-relaxed max-w-xl"
              style={{ color: "var(--ls-muted)" }}
              data-testid="consulting-intro"
            >
              Lancers Consulting is the direct service arm of Lancers Studio. We
              provide skilled lancers and technical experts to fill your vacant
              positions for a defined contract period, helping your organization
              continue operations without delay.
            </p>

            <ul className="mt-8 space-y-4 max-w-xl" data-testid="consulting-bullets">
              {bullets.map((b) => (
                <li key={b} className="ls-bullet">
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div
              className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5"
              data-testid="consulting-info-boxes"
            >
              {supportBlocks.map((s) => (
                <div key={s.title} className="ls-card px-5 py-5">
                  <h3
                    className="font-cinzel gold-text text-[0.95rem] uppercase mb-2"
                    style={{ letterSpacing: "0.14em" }}
                  >
                    {s.title}
                  </h3>
                  <p
                    className="text-[0.88rem] leading-relaxed"
                    style={{ color: "var(--ls-muted)" }}
                  >
                    {s.body}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — Form */}
          <div className="lg:col-span-5 ls-fade-up" style={{ animationDelay: "120ms" }}>
            <div
              className="ls-card-strong relative px-6 sm:px-8 py-8 lg:sticky lg:top-28"
              data-testid="consulting-form-card"
            >
              <h2
                className="font-cinzel gold-text"
                style={{
                  fontSize: "1.6rem",
                  letterSpacing: "0.08em",
                }}
              >
                Request Lancers
              </h2>
              <p
                className="mt-2 text-sm"
                style={{ color: "var(--ls-muted)" }}
              >
                Share your requirement and contact details.
              </p>

              {submitted ? (
                <div className="mt-7 ls-success" data-testid="consulting-success">
                  Requirement submitted successfully. We will call shortly —
                  don't worry, we are here.
                </div>
              ) : (
                <form
                  className="mt-6 space-y-4"
                  onSubmit={onSubmit}
                  data-testid="consulting-form"
                  noValidate
                >
                  {/* Honeypot: visually hidden, ignored by real users, filled by bots. */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: "-9999px",
                      width: 1,
                      height: 1,
                      overflow: "hidden",
                    }}
                  >
                    <label htmlFor="website">Website</label>
                    <input
                      tabIndex={-1}
                      autoComplete="off"
                      id="website"
                      name="website"
                      type="text"
                      value={form.website}
                      onChange={onChange}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="ls-label">Organization Name</label>
                      <input
                        className="ls-input"
                        name="organisation_name"
                        value={form.organisation_name}
                        onChange={onChange}
                        placeholder="e.g. Apex Facades Pvt Ltd"
                        data-testid="consulting-input-organization"
                        disabled={submitting}
                      />
                    </div>
                    <div>
                      <label className="ls-label">Contact Person</label>
                      <input
                        className="ls-input"
                        name="contact_person"
                        value={form.contact_person}
                        onChange={onChange}
                        placeholder="Full name"
                        data-testid="consulting-input-contact-person"
                        disabled={submitting}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="ls-label">Contact Number</label>
                      <input
                        className="ls-input"
                        name="contact_number"
                        value={form.contact_number}
                        onChange={onChange}
                        placeholder="+91 XXXXX XXXXX"
                        data-testid="consulting-input-number"
                        disabled={submitting}
                      />
                    </div>
                    <div>
                      <label className="ls-label">Email Address</label>
                      <input
                        className="ls-input"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={onChange}
                        placeholder="you@company.com"
                        data-testid="consulting-input-email"
                        disabled={submitting}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="ls-label">Reason to Select Lancers</label>
                    <textarea
                      className="ls-textarea"
                      rows={4}
                      name="requirement"
                      value={form.requirement}
                      onChange={onChange}
                      placeholder="Tell us about the role, timeline, and expertise required."
                      data-testid="consulting-input-reason"
                      disabled={submitting}
                    />
                  </div>

                  {error && (
                    <p
                      className="text-sm"
                      style={{ color: "#f87171" }}
                      data-testid="consulting-error"
                    >
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    className="ls-btn-gold w-full mt-2"
                    disabled={submitting}
                    data-testid="consulting-submit"
                    aria-busy={submitting}
                  >
                    <span>
                      {submitting ? "Submitting…" : "Submit Requirement"}
                    </span>
                  </button>
                </form>
              )}

              <p
                className="mt-6 text-[0.78rem] leading-relaxed"
                style={{ color: "var(--ls-muted-2)" }}
                data-testid="consulting-note"
              >
                Need urgent support? Mention your expected joining timeline and
                required expertise in the form.
              </p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Consulting;
